import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Save, Upload, Trash2, Image as ImageIcon, Video } from "lucide-react";
import { api, formatApiError } from "../api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const planUrl = (id) => `${BACKEND}/api/plans/photos/${id}`;
const ACCEPT = ".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp";

export function SettingsAdmin() {
  const [videoUrl, setVideoUrl] = useState("");
  const [plans, setPlans] = useState([]);
  const [savingVideo, setSavingVideo] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [label, setLabel] = useState("");
  const inputRef = useRef(null);

  const load = () =>
    api.get("/settings").then((r) => {
      setVideoUrl(r.data?.virtual_tour_url || "");
      setPlans(r.data?.plans || []);
    }).catch(() => {});

  useEffect(() => { load(); }, []);

  const saveVideo = async () => {
    setSavingVideo(true);
    try {
      await api.put("/admin/settings", { virtual_tour_url: videoUrl.trim() });
      toast.success("Recorrido virtual actualizado");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSavingVideo(false);
    }
  };

  const uploadPlan = async (fileList) => {
    const file = fileList?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("label", label.trim() || file.name.replace(/\.[^.]+$/, ""));
    setUploading(true);
    try {
      const { data } = await api.post("/admin/plans", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPlans(data.plans);
      setLabel("");
      toast.success("Plano subido");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const removePlan = async (id) => {
    if (!window.confirm("¿Eliminar este plano?")) return;
    try {
      const { data } = await api.delete(`/admin/plans/${id}`);
      setPlans(data.plans);
      toast.success("Plano eliminado");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-settings">
      <h1 className="text-3xl font-bold text-black">Configuración del sitio</h1>
      <p className="mt-2 text-sm font-light text-black/50">
        Gestiona el recorrido virtual y los planos del inmueble que se muestran en la página pública.
      </p>

      {/* Recorrido virtual */}
      <div className="mt-8 bg-white border border-[#B2967D]/25 p-8 max-w-3xl">
        <div className="flex items-center gap-2 text-[#7D5A44]">
          <Video size={18} />
          <h2 className="text-lg font-bold">Recorrido virtual</h2>
        </div>
        <p className="mt-2 text-sm font-light text-black/50">
          Pega la URL <strong>embed</strong> del video (YouTube, Vimeo o Matterport). Ej: https://www.youtube.com/embed/ID
        </p>
        <input
          data-testid="settings-video-input"
          value={videoUrl}
          onChange={(e) => setVideoUrl(e.target.value)}
          placeholder="https://www.youtube.com/embed/..."
          className="mt-4 w-full border border-[#B2967D]/40 px-3 py-2.5 text-sm focus:outline-none focus:border-[#7D5A44]"
        />
        <button
          onClick={saveVideo}
          disabled={savingVideo}
          data-testid="settings-video-save"
          className="mt-4 inline-flex items-center gap-2 bg-[#7D5A44] text-white px-5 py-3 text-xs font-bold uppercase tracking-wide hover:bg-black transition-colors disabled:opacity-60"
        >
          <Save size={16} /> {savingVideo ? "Guardando..." : "Guardar URL"}
        </button>
        {videoUrl && (
          <div className="mt-6 aspect-video w-full border border-[#B2967D]/30">
            <iframe title="preview" src={videoUrl} className="w-full h-full" style={{ border: 0 }} allowFullScreen />
          </div>
        )}
      </div>

      {/* Planos */}
      <div className="mt-8 bg-white border border-[#B2967D]/25 p-8 max-w-3xl">
        <div className="flex items-center gap-2 text-[#7D5A44]">
          <ImageIcon size={18} />
          <h2 className="text-lg font-bold">Planos del inmueble</h2>
        </div>
        <p className="mt-2 text-sm font-light text-black/50">
          Sube imágenes de los planos (ej. Planta Baja, Nivel 1, Nivel 2). Se muestran como slider en Ubicación.
        </p>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <input
            data-testid="settings-plan-label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Nombre del plano (ej. Planta Baja)"
            className="flex-1 min-w-[200px] border border-[#B2967D]/40 px-3 py-2.5 text-sm focus:outline-none focus:border-[#7D5A44]"
          />
          <input ref={inputRef} type="file" accept={ACCEPT} className="hidden" data-testid="settings-plan-file" onChange={(e) => uploadPlan(e.target.files)} />
          <button
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            data-testid="settings-plan-upload"
            className="inline-flex items-center gap-2 bg-[#7D5A44] text-white px-5 py-3 text-xs font-bold uppercase tracking-wide hover:bg-black transition-colors disabled:opacity-60"
          >
            <Upload size={16} /> {uploading ? "Subiendo..." : "Subir plano"}
          </button>
        </div>

        <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-4">
          {plans.map((p, i) => (
            <div key={p.id} data-testid={`settings-plan-${i}`} className="relative group border border-[#B2967D]/30">
              <img src={planUrl(p.id)} alt={p.label} className="w-full h-32 object-cover" />
              <span className="absolute bottom-0 inset-x-0 bg-black/55 text-white text-xs px-2 py-1 truncate">{p.label}</span>
              <button
                onClick={() => removePlan(p.id)}
                data-testid={`settings-plan-delete-${i}`}
                className="absolute top-2 right-2 bg-white/90 text-red-600 p-1.5 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
          {plans.length === 0 && (
            <div className="col-span-full text-center py-10 text-sm font-light text-black/40 border border-dashed border-[#B2967D]/40">
              Aún no hay planos subidos.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
